import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Ensures user is authenticated before rendering children
 * Redirects to tenant-specific login page if no valid token found
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  
  // Check for authentication token
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  const userInfo = localStorage.getItem('userInfo');
  
  // If no token or no user info, redirect to login
  if (!token || !userInfo) {
    // Get tenant slug from stored user info or URL params or location state
    let loginPath = '/login';
    
    // Try to get tenant slug from URL
    if (tenantSlug) {
      loginPath = `/t/${tenantSlug}/login`;
    } else {
      // Try to get from stored tenant info
      const storedTenantInfo = localStorage.getItem('tenantInfo');
      if (storedTenantInfo) {
        try {
          const tenant = JSON.parse(storedTenantInfo);
          if (tenant.slug) {
            loginPath = `/t/${tenant.slug}/login`;
          }
        } catch {
          // Ignore parse errors
        }
      }
    }
    
    // Save the attempted URL for redirecting after login
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  
  // Validate that userInfo is parseable
  try {
    JSON.parse(userInfo);
  } catch {
    // Invalid userInfo, clear and redirect
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    
    // Try to redirect to tenant login
    let loginPath = '/login';
    if (tenantSlug) {
      loginPath = `/t/${tenantSlug}/login`;
    }
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

export default ProtectedRoute;
